import config, { apiUrl } from '../../config';

import { useNavigate, useParams } from 'react-router-dom';
import React, { useMemo, useState, useEffect } from 'react';
import Loading from '../../components/shared/Loading';
import { useAuth } from '../../hooks/use-auth';
import {
  Avatar,
  Typography,
  Tabs,
  Box,
  Tab,
  Button,
  Card,
  Chip,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import FlameIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import Page from '../Page';
import ActivityTab from './ActivityTab';
import CalendarTab from './CalendarTab';
import NotesTab from './NotesTab';
import ChatTab from './ChatTab';
import NutritionTab from './NutritionTab';
import SubscriptionTab from './SubscriptionTab';

const tabs = [
  { value: 'overview', label: 'Overview', icon: <PersonOutlineIcon /> },
  { value: 'activity', label: 'Activity', icon: <FitnessCenterOutlinedIcon /> },
  { value: 'chat', label: 'Messages', icon: <ChatBubbleOutlineIcon /> },
  { value: 'calendar', label: 'Schedule', icon: <CalendarTodayOutlinedIcon /> },
  { value: 'nutrition', label: 'Nutrition', icon: <RestaurantOutlinedIcon /> },
  { value: 'notes', label: 'Notes', icon: <NotesOutlinedIcon /> },
  { value: 'subscription', label: 'Billing', icon: <CreditCardOutlinedIcon /> },
];

const cardSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
};

const iconTileSx = {
  alignItems: 'center',
  borderRadius: 1.5,
  display: 'inline-flex',
  height: 32,
  justifyContent: 'center',
  mr: 1.5,
  width: 32,
};

const legacyTabMap = {
  messages: 'chat',
  billing: 'subscription',
  schedule: 'calendar',
};

const formatDate = (date, options = {}) => {
  if (!date) {
    return '-';
  }

  const value = new Date(date);
  if (Number.isNaN(value.getTime())) {
    return date;
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options,
  }).format(value);
};

const formatDateTime = (date) =>
  formatDate(date, { hour: 'numeric', minute: '2-digit' });

const getRelativeDays = (date) => {
  if (!date) {
    return null;
  }

  const value = new Date(date);
  if (Number.isNaN(value.getTime())) {
    return null;
  }

  return Math.max(
    0,
    Math.floor((Date.now() - value.getTime()) / (1000 * 60 * 60 * 24))
  );
};

const toFiniteNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const getValidDate = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfWeek = (date) => {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  value.setDate(value.getDate() - value.getDay());
  return value;
};

const addDays = (date, days) => {
  const value = new Date(date);
  value.setDate(value.getDate() + days);
  return value;
};

const formatShortDate = (date) =>
  new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(date);

const getActivityDate = (activity = {}) =>
  getValidDate(
    activity.createdAt ||
      activity.workoutSession?.completedAt ||
      activity.workoutSession?.createdAt ||
      activity.mealLog?.createdAt
  );

const getWorkoutSessionDate = (workoutSession = {}) =>
  getValidDate(workoutSession.completedAt || workoutSession.createdAt);

const buildWeeklyProgressData = ({
  workoutSessions = [],
  activities = [],
  mode,
}) => {
  const currentWeekStart = startOfWeek(new Date());
  const firstWeekStart = addDays(currentWeekStart, -77);
  const buckets = Array.from({ length: 12 }).map((_, index) => {
    const start = addDays(firstWeekStart, index * 7);
    const end = addDays(start, 6);

    return {
      count: 0,
      end,
      label: formatShortDate(start),
      start,
    };
  });

  const source =
    mode === 'activity'
      ? activities.map(getActivityDate)
      : workoutSessions.map(getWorkoutSessionDate);

  source.forEach((date) => {
    if (
      !date ||
      date < firstWeekStart ||
      date >= addDays(currentWeekStart, 7)
    ) {
      return;
    }

    const weekIndex = Math.floor(
      (startOfWeek(date) - firstWeekStart) / 604800000
    );
    if (weekIndex >= 0 && weekIndex < buckets.length) {
      buckets[weekIndex].count += 1;
    }
  });

  return buckets;
};

const getActivityIcon = (activity = {}) => {
  if (activity.workoutSession) {
    return <CheckCircleIcon fontSize="small" />;
  }

  if (activity.mealLog) {
    return <RestaurantOutlinedIcon fontSize="small" />;
  }

  return <ChatBubbleOutlineIcon fontSize="small" />;
};

const getActivityColor = (activity = {}, index) => {
  if (activity.workoutSession) {
    return '#16a34a';
  }

  if (activity.mealLog) {
    return '#f59e0b';
  }

  return index % 2 === 0 ? '#4f46e5' : '#7c3aed';
};

const findActivePlan = (trainingPlanSessions = []) =>
  trainingPlanSessions.find((session) => session.status === 'ACTIVE') ||
  trainingPlanSessions[0];

function MetricCard({ title, value, detail, icon, color, children }) {
  return (
    <Card sx={{ ...cardSx, p: 2.5, minHeight: 128 }}>
      <Box display="flex" justifyContent="space-between" gap={2}>
        <Box minWidth={0}>
          <Typography color="text.primary" fontWeight={700} variant="subtitle2">
            {title}
          </Typography>
          <Typography color={color} fontWeight={800} mt={1} variant="h3">
            {value}
          </Typography>
          {detail && (
            <Typography color="text.secondary" mt={1} variant="body2">
              {detail}
            </Typography>
          )}
        </Box>
        {icon && <Box color={color}>{icon}</Box>}
      </Box>
      {children && <Box mt={1.5}>{children}</Box>}
    </Card>
  );
}

function SectionHeader({ icon, title, action }) {
  return (
    <Box
      alignItems="center"
      display="flex"
      justifyContent="space-between"
      mb={2}
    >
      <Box alignItems="center" display="flex">
        <Box sx={{ ...iconTileSx, bgcolor: '#eef2ff', color: '#4f46e5' }}>
          {icon}
        </Box>
        <Typography fontWeight={800} variant="h6">
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
  );
}

function OverviewTab({
  user,
  activities,
  notes,
  trainingPlanSessions,
  workoutSessions,
  onTabChange,
}) {
  const activePlan = findActivePlan(trainingPlanSessions);
  const completedWorkouts = toFiniteNumber(user.workoutSessionCount);
  const activePlanEventCount = toFiniteNumber(activePlan?.totalEventCount);
  const totalWorkouts = Math.max(completedWorkouts, activePlanEventCount);
  const adherence = totalWorkouts
    ? Math.min(100, Math.round((completedWorkouts / totalWorkouts) * 100))
    : 0;
  const completedEvents = toFiniteNumber(
    activePlan?.completedEventCount,
    completedWorkouts
  );
  const planTotalEvents = activePlanEventCount || Math.max(completedEvents, 1);
  const planProgress = Math.min(
    100,
    Math.round((completedEvents / planTotalEvents) * 100)
  );
  const latestWorkout = workoutSessions?.[0];
  const lastActiveDays =
    getRelativeDays(latestWorkout?.completedAt || latestWorkout?.createdAt) ??
    getRelativeDays(user.updatedAt || user.lastActiveAt);
  const recentActivities = (activities || []).slice(0, 5);
  const recentNotes = (notes || []).slice(0, 2);
  const [progressMetric, setProgressMetric] = useState('workouts');
  const progressData = useMemo(
    () =>
      buildWeeklyProgressData({
        activities,
        mode: progressMetric,
        workoutSessions,
      }),
    [activities, progressMetric, workoutSessions]
  );
  const maxProgressCount = Math.max(
    ...progressData.map((item) => item.count),
    0
  );
  const totalProgressCount = progressData.reduce(
    (total, item) => total + item.count,
    0
  );
  const progressUnit = progressMetric === 'workouts' ? 'workout' : 'activity';

  return (
    <Box
      display="grid"
      gap={2.5}
      gridTemplateColumns={{ xs: '1fr', lg: 'minmax(0, 1fr) 340px' }}
    >
      <Box minWidth={0}>
        <Box
          display="grid"
          gap={2.5}
          gridTemplateColumns={{
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            xl: '1.25fr 0.75fr 1.1fr 0.85fr',
          }}
          mb={2.5}
        >
          <MetricCard
            color="#16a34a"
            detail={`${completedWorkouts} workouts completed`}
            title="Training Adherence"
            value={`${adherence}%`}
          />
          <MetricCard
            color="#f97316"
            detail="Keep it up!"
            icon={<FlameIcon />}
            title="Current Streak"
            value={latestWorkout ? '1 day' : '0 days'}
          />
          <MetricCard
            color="#2563eb"
            detail={`${completedEvents} of ${planTotalEvents} workouts completed`}
            title="Program Progress"
            value={`${planProgress}%`}
          >
            <LinearProgress
              variant="determinate"
              value={planProgress}
              sx={{ height: 8, bgcolor: '#e5e7eb' }}
            />
          </MetricCard>
          <MetricCard
            color="#4f46e5"
            detail={
              <Button
                onClick={() => onTabChange('calendar')}
                sx={{ px: 0, minWidth: 0, fontWeight: 800 }}
              >
                Schedule now
              </Button>
            }
            icon={<CalendarTodayOutlinedIcon />}
            title="Next Session"
            value={activePlan?.nextEvent?.title || 'None'}
          />
        </Box>

        <Box
          display="grid"
          gap={2.5}
          gridTemplateColumns={{ xs: '1fr', md: '1.1fr 0.9fr' }}
        >
          <Card sx={{ ...cardSx, p: 3 }}>
            <SectionHeader
              icon={<FitnessCenterOutlinedIcon fontSize="small" />}
              title="Activity Timeline"
              action={
                <Select defaultValue="all" size="small" sx={{ minWidth: 130 }}>
                  <MenuItem value="all">All Activity</MenuItem>
                  <MenuItem value="workouts">Workouts</MenuItem>
                  <MenuItem value="nutrition">Nutrition</MenuItem>
                </Select>
              }
            />
            <Stack spacing={2.25}>
              {recentActivities.length === 0 && (
                <Typography color="text.secondary" variant="body2">
                  No recent activity.
                </Typography>
              )}
              {recentActivities.map((activity, index) => {
                const color = getActivityColor(activity, index);
                return (
                  <Box
                    display="grid"
                    gridTemplateColumns="36px minmax(0, 1fr)"
                    key={activity._id || index}
                  >
                    <Box position="relative">
                      <Box
                        sx={{
                          alignItems: 'center',
                          bgcolor: color,
                          borderRadius: '50%',
                          color: 'white',
                          display: 'flex',
                          height: 28,
                          justifyContent: 'center',
                          width: 28,
                          zIndex: 1,
                        }}
                      >
                        {getActivityIcon(activity)}
                      </Box>
                      {index < recentActivities.length - 1 && (
                        <Box
                          sx={{
                            bgcolor: '#e5e7eb',
                            bottom: -18,
                            left: 13,
                            position: 'absolute',
                            top: 28,
                            width: 2,
                          }}
                        />
                      )}
                    </Box>
                    <Box minWidth={0}>
                      <Typography fontWeight={800} variant="subtitle2">
                        {activity.title || 'Activity'}
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        {formatDateTime(activity.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
            <Button
              onClick={() => onTabChange('activity')}
              sx={{ mt: 3, px: 0, fontWeight: 800 }}
            >
              View all activity
            </Button>
          </Card>

          <Card sx={{ ...cardSx, p: 3 }}>
            <SectionHeader
              icon={<NotesOutlinedIcon fontSize="small" />}
              title="Recent Notes"
              action={
                <Button
                  onClick={() => onTabChange('notes')}
                  sx={{ fontWeight: 800 }}
                >
                  Add Note
                </Button>
              }
            />
            <Stack spacing={2}>
              {recentNotes.length === 0 && (
                <Typography color="text.secondary" variant="body2">
                  No notes yet.
                </Typography>
              )}
              {recentNotes.map((note) => (
                <Box
                  border="1px solid"
                  borderColor="divider"
                  borderRadius={1.5}
                  key={note._id}
                  p={2}
                >
                  <Typography color="text.secondary" mb={1} variant="caption">
                    {formatDate(note.createdAt)}
                  </Typography>
                  <Typography color="text.primary" variant="body2">
                    {note.body}
                  </Typography>
                  <Typography color="text.secondary" mt={1.5} variant="caption">
                    Private
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Button
              onClick={() => onTabChange('notes')}
              sx={{ mt: 2.5, px: 0, fontWeight: 800 }}
            >
              View all notes
            </Button>
          </Card>
        </Box>

        <Card sx={{ ...cardSx, p: 3, mt: 2.5 }}>
          <SectionHeader
            icon={<InsertChartOutlinedIcon fontSize="small" />}
            title="Progress Overview"
            action={
              <Select
                onChange={(event) => setProgressMetric(event.target.value)}
                size="small"
                sx={{ minWidth: 150 }}
                value={progressMetric}
              >
                <MenuItem value="workouts">Workouts</MenuItem>
                <MenuItem value="activity">Activity</MenuItem>
              </Select>
            }
          />
          <Box
            alignItems="baseline"
            display="flex"
            flexWrap="wrap"
            gap={1}
            mb={2}
          >
            <Typography fontWeight={800} variant="h5">
              {totalProgressCount}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {progressUnit}
              {totalProgressCount === 1 ? '' : 's'} in the last 12 weeks
            </Typography>
          </Box>
          <Box
            alignItems="end"
            display="grid"
            gap={1}
            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
            height={130}
            sx={{
              backgroundImage:
                'linear-gradient(to bottom, transparent 24%, #e5e7eb 25%, transparent 26%, transparent 49%, #e5e7eb 50%, transparent 51%, transparent 74%, #e5e7eb 75%, transparent 76%)',
            }}
          >
            {progressData.map((bucket) => (
              <Box
                key={bucket.start.toISOString()}
                title={`${bucket.count} ${progressUnit}${
                  bucket.count === 1 ? '' : 's'
                } during ${formatShortDate(bucket.start)}-${formatShortDate(
                  bucket.end
                )}`}
                sx={{
                  bgcolor: '#4f46e5',
                  borderRadius: 1,
                  height: maxProgressCount
                    ? `${Math.max(
                        12,
                        (bucket.count / maxProgressCount) * 100
                      )}%`
                    : 12,
                  opacity: bucket.count ? 0.85 : 0.18,
                }}
              />
            ))}
          </Box>
          <Box
            color="text.secondary"
            display="grid"
            fontSize={12}
            gap={1}
            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
            mt={1}
          >
            {progressData.map((bucket, index) => (
              <Typography
                align="center"
                color="text.secondary"
                key={bucket.start.toISOString()}
                sx={{ fontSize: 11 }}
                variant="caption"
              >
                {index % 2 === 0 || index === progressData.length - 1
                  ? bucket.label
                  : ''}
              </Typography>
            ))}
          </Box>
        </Card>
      </Box>

      <Stack spacing={2.5} minWidth={0}>
        <Card sx={{ ...cardSx, p: 3 }}>
          <SectionHeader
            icon={<FavoriteBorderOutlinedIcon fontSize="small" />}
            title="Client Health"
          />
          <Chip
            color="success"
            label={
              lastActiveDays != null && lastActiveDays <= 7
                ? 'Engaged'
                : 'Watch'
            }
            size="small"
            sx={{ fontWeight: 800, mb: 1 }}
          />
          <Typography color="text.secondary" mb={2} variant="body2">
            Everything looks good.
          </Typography>
          {[
            lastActiveDays != null
              ? `Active in the last ${lastActiveDays || 1} days`
              : 'No recent activity found',
            'Training regularly',
            'Following nutrition plan',
          ].map((item) => (
            <Box
              alignItems="center"
              display="flex"
              gap={1.5}
              key={item}
              mb={1.25}
            >
              <Box
                sx={{
                  bgcolor: '#22c55e',
                  borderRadius: '50%',
                  height: 8,
                  width: 8,
                }}
              />
              <Typography variant="body2">{item}</Typography>
            </Box>
          ))}
        </Card>

        <Card sx={{ ...cardSx, p: 3 }}>
          <SectionHeader
            icon={<FitnessCenterOutlinedIcon fontSize="small" />}
            title="Active Plan"
          />
          {activePlan ? (
            <>
              <Box alignItems="center" display="flex" gap={1} flexWrap="wrap">
                <Typography fontWeight={800} variant="subtitle1">
                  {activePlan.trainingPlan?.title || 'Training Plan'}
                </Typography>
                <Chip
                  color="success"
                  label={activePlan.status || 'ACTIVE'}
                  size="small"
                  sx={{ fontWeight: 800 }}
                />
              </Box>
              <Typography color="text.secondary" mt={1} variant="body2">
                Started {formatDate(activePlan.enrolledAt)}
              </Typography>
              <Button
                onClick={() => onTabChange('activity')}
                sx={{ mt: 2, px: 0, fontWeight: 800 }}
              >
                View plan details
              </Button>
            </>
          ) : (
            <>
              <Typography color="text.secondary" variant="body2">
                No active training plan.
              </Typography>
              <Button
                onClick={() => onTabChange('activity')}
                sx={{ mt: 2, px: 0, fontWeight: 800 }}
              >
                Assign a plan
              </Button>
            </>
          )}
        </Card>

        <Card sx={{ ...cardSx, p: 3 }}>
          <Box
            alignItems="center"
            display="flex"
            justifyContent="space-between"
          >
            <Box alignItems="center" display="flex">
              <WarningAmberOutlinedIcon color="warning" sx={{ mr: 1 }} />
              <Typography fontWeight={800} variant="subtitle1">
                Needs Attention
              </Typography>
            </Box>
            <Typography color="text.secondary" variant="caption">
              {workoutSessions?.length ? '0 items' : '1 item'}
            </Typography>
          </Box>
          {!workoutSessions?.length && (
            <Box bgcolor="#fff7ed" borderRadius={1.5} mt={2} p={2}>
              <Typography fontWeight={800} variant="body2">
                No upcoming sessions scheduled
              </Typography>
              <Typography color="text.secondary" variant="caption">
                Schedule a session to keep momentum
              </Typography>
              <br />
              <Button
                onClick={() => onTabChange('calendar')}
                sx={{ px: 0, fontWeight: 800 }}
              >
                Schedule now
              </Button>
            </Box>
          )}
        </Card>

        <Card sx={{ ...cardSx, p: 3 }}>
          <Box
            alignItems="center"
            display="flex"
            justifyContent="space-between"
            mb={2}
          >
            <Box alignItems="center" display="flex">
              <PersonOutlineIcon color="primary" sx={{ mr: 1 }} />
              <Typography fontWeight={800} variant="subtitle1">
                Client Info
              </Typography>
            </Box>
            <Button sx={{ minWidth: 0, p: 0, fontWeight: 800 }}>
              View all
            </Button>
          </Box>
          {[
            ['Email', user.email || '-'],
            ['Timezone', user.timezone || '-'],
            ['Location', user.location || '-'],
          ].map(([label, value]) => (
            <Box key={label} mb={1.25}>
              <Typography color="text.secondary" variant="caption">
                {label}
              </Typography>
              <Typography sx={{ overflowWrap: 'anywhere' }} variant="body2">
                {value}
              </Typography>
            </Box>
          ))}
        </Card>
      </Stack>
    </Box>
  );
}

export default function UserShow() {
  const [isLoading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState('overview');

  const [user, setUser] = useState(null);
  const [activities, setActivities] = useState([]);
  const [subscriptionOrders, setSubscriptionOrders] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [notes, setNotes] = useState([]);
  const [subscriptionEvents, setSubscriptionEvents] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [trainingPlanSessions, setTrainingPlanSessions] = useState([]);
  const [workoutSessions, setWorkoutSessions] = useState([]);

  const [token, setToken] = useState(null);
  const auth = useAuth();
  const authLoading = auth.loading;
  const loggedInUser = auth.user;

  const params = useParams();
  const navigate = useNavigate();
  const id = params.id;
  const handleTabChange = (value) => {
    setCurrentTab(value);
    navigate(`/trainer/users/${id}/${value}`);
  };
  const handleTabsChange = (event, value) => handleTabChange(value);

  const handleTrainingPlanSessionStarted = (trainingPlanSession) => {
    if (!trainingPlanSession?._id) {
      return;
    }

    setTrainingPlanSessions((currentSessions) => {
      const existingIndex = currentSessions.findIndex(
        (session) => session._id === trainingPlanSession._id
      );

      if (existingIndex === -1) {
        return [trainingPlanSession, ...currentSessions];
      }

      return currentSessions.map((session, index) =>
        index === existingIndex ? trainingPlanSession : session
      );
    });
  };

  useEffect(() => {
    if (params.tab) {
      setCurrentTab(legacyTabMap[params.tab] || params.tab);
    } else {
      setCurrentTab('overview');
    }
  }, [params]);

  useEffect(() => {
    setLoading(true);
    loggedInUser
      .getIdToken()
      .then((token) => {
        setToken(token);
        if (!id) {
          throw new Error('No id');
        }
        return fetch(`${apiUrl}/api/admin/users/${id}`, {
          method: 'GET',
          headers: new Headers({ Authorization: `Bearer ${token}` }),
        });
      })
      .then((res) => res.json())
      .then((response) => {
        setLoading(false);
        if (!response.user) {
          window.alert('No user found');
          return;
        }
        setUser(response.user);

        setActivities(response.activities);
        setNotes(response.notes);
        setWorkoutSessions(response.workoutSessions);
        setTrainingPlanSessions(response.trainingPlanSessions);
        setSubscriptionOrders(response.subscriptionOrders);
        setSubscriptions(response.subscriptions);
        setSubscriptionEvents(response.subscriptionEvents);
        setPurchases(response.purchases);
      });
  }, [loggedInUser, id]);

  if (!id) {
    return <h3>No id</h3>;
  }

  if (authLoading || isLoading) {
    return <Loading />;
  }

  if (!user) {
    return <h1>No user</h1>;
  }

  const lastActiveSession = workoutSessions?.[0];
  const lastActiveDays =
    getRelativeDays(
      lastActiveSession?.completedAt ||
        lastActiveSession?.createdAt ||
        user.updatedAt ||
        user.lastActiveAt
    ) ?? null;

  return (
    <Page title={`${user.name || user._id} : ${config.appDisplayName}`}>
      <Box bgcolor="#f8fafc" minHeight="100vh">
        <Box
          bgcolor="background.paper"
          borderBottom="1px solid"
          borderColor="divider"
          px={{ xs: 2, md: 3 }}
          pt={2.5}
        >
          <Box
            alignItems={{ xs: 'flex-start', md: 'center' }}
            display="flex"
            flexDirection={{ xs: 'column', md: 'row' }}
            gap={2}
            justifyContent="space-between"
          >
            <Box alignItems="center" display="flex" gap={2} minWidth={0}>
              <IconButton
                aria-label="Back"
                onClick={() => navigate('/trainer/users')}
                size="small"
              >
                <ArrowBackIcon />
              </IconButton>
              <Avatar
                alt={user.name || 'User'}
                src={user.avatarUrl}
                sx={{
                  bgcolor: '#111827',
                  border: '1px solid',
                  borderColor: 'divider',
                  height: 82,
                  width: 82,
                }}
              />
              <Box minWidth={0}>
                <Box alignItems="center" display="flex" flexWrap="wrap" gap={1}>
                  <Typography fontWeight={900} variant="h3">
                    {user.name || user._id}
                  </Typography>
                  <Chip color="success" label="Active" size="small" />
                </Box>
                <Box
                  color="text.secondary"
                  display="flex"
                  flexWrap="wrap"
                  gap={2.5}
                  mt={1}
                >
                  <Box alignItems="center" display="flex" gap={0.75}>
                    <EventNoteOutlinedIcon fontSize="small" />
                    <Typography variant="body2">
                      Joined {formatDate(user.createdAt)}
                    </Typography>
                  </Box>
                  <Box alignItems="center" display="flex" gap={0.75}>
                    <ScheduleOutlinedIcon fontSize="small" />
                    <Typography variant="body2">
                      {lastActiveDays != null
                        ? `Last active ${lastActiveDays || 1} days ago`
                        : 'Last active unknown'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
              justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
              useFlexGap
            >
              <Button
                onClick={() => handleTabChange('chat')}
                startIcon={<SendOutlinedIcon />}
                variant="contained"
              >
                Message
              </Button>
              <Button
                onClick={() => handleTabChange('calendar')}
                startIcon={<CalendarTodayOutlinedIcon />}
                variant="outlined"
              >
                Schedule
              </Button>
              <Button
                onClick={() => handleTabChange('activity')}
                startIcon={<FitnessCenterOutlinedIcon />}
                variant="outlined"
              >
                Assign Plan
              </Button>
              <Button
                onClick={() => handleTabChange('notes')}
                startIcon={<EditNoteOutlinedIcon />}
                variant="outlined"
              >
                Add Note
              </Button>
              <IconButton
                aria-label="More actions"
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                <MoreVertIcon />
              </IconButton>
            </Stack>
          </Box>

          <Tabs
            onChange={handleTabsChange}
            scrollButtons="auto"
            sx={{
              mt: 2.5,
              minHeight: 48,
              '& .MuiTab-root': {
                fontWeight: 800,
                minHeight: 48,
                textTransform: 'none',
              },
            }}
            value={currentTab}
            variant="scrollable"
            textColor="secondary"
            indicatorColor="secondary"
          >
            {tabs.map((tab) => (
              <Tab
                icon={tab.icon}
                iconPosition="start"
                key={tab.value}
                label={tab.label}
                value={tab.value}
              />
            ))}
          </Tabs>
        </Box>

        <Box px={{ xs: 2, md: 3 }} py={2.5}>
          {currentTab === 'overview' && (
            <OverviewTab
              user={user}
              activities={activities}
              notes={notes}
              workoutSessions={workoutSessions}
              trainingPlanSessions={trainingPlanSessions}
              onTabChange={handleTabChange}
            />
          )}
          {currentTab === 'activity' && (
            <ActivityTab
              token={token}
              user={user}
              trainingPlanSessions={trainingPlanSessions}
              activities={activities}
              notes={notes}
              onTabChange={handleTabChange}
              onTrainingPlanSessionStarted={handleTrainingPlanSessionStarted}
            />
          )}
          {currentTab === 'calendar' && (
            <CalendarTab user={user} workoutSessions={workoutSessions} />
          )}
          {currentTab === 'notes' && <NotesTab user={user} notes={notes} />}
          {currentTab === 'chat' && <ChatTab user={user} />}
          {currentTab === 'nutrition' && <NutritionTab user={user} />}

          {currentTab === 'subscription' && (
            <SubscriptionTab
              token={token}
              user={user}
              subscriptionOrders={subscriptionOrders}
              subscriptions={subscriptions}
              subscriptionEvents={subscriptionEvents}
              purchases={purchases}
            />
          )}
        </Box>
      </Box>
    </Page>
  );
}
